<template>
  <el-dialog
    class="qm-import"
    title="导入"
    :width="dialogWidth"
    append-to-body
    :close-on-press-escape="false"
    :close-on-click-modal="false"
    v-bind="$attrs"
    v-on="$listeners"
    @closed="onClose"
  >
    <el-steps :active="stepActive" align-center finish-status="success">
      <el-step title="上传文件" />
      <el-step title="数据导入" />
      <el-step title="完成导入" />
    </el-steps>

    <template v-if="currentStatus === 0">
      <div class="qm-import__prepend">
        <slot name="prepend" />
      </div>

      <div :class="{ 'qm-import__upload': true, 'qm-import__upload--disabled': $uploadDisabled }">
        <p class="qm-import__upload-download">
          按照表格上方提示录入待导入的信息
          <span class="qm-import__upload-template" @click="onDownloadTemplate"> 下载模版 </span>
        </p>
        <el-upload
          drag
          action=""
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/csv"
          :disabled="$uploadDisabled"
          :auto-upload="false"
          :show-file-list="false"
          :on-change="onUploadChange"
        >
          <i class="qm-import__upload-icon qm-icon-upload-2"></i>
          <div class="qm-import__upload-tip">支持文件拖拽至框内上传</div>
          <el-button size="medium" plain>选择文件</el-button>
        </el-upload>
      </div>
      <div class="qm-import__tip">
        <div class="qm-import__tip-scroll">
          <slot name="notice" />
        </div>
      </div>
    </template>

    <template v-else-if="currentStatus === 1 || currentStatus === 2">
      <div class="qm-import__alert">
        预计导入{{ alertInfo.allNum }}条数据，其中{{
          alertInfo.errNum
        }}条可能有误，请按照提示调整后再导入。（有误数据，将会跳过不会导入系统）
      </div>

      <el-table
        class="qm-import__table"
        :data="paginationData"
        max-height="360px"
        v-loading="currentStatus === 1"
        element-loading-text="数据解析中..."
      >
        <el-table-column
          v-for="(column, index) in columns"
          :key="index"
          :label="column.label"
          :prop="column.label"
          :min-width="column.minWidth"
          show-overflow-tooltip
        >
          <template slot-scope="{ row }">
            <div v-if="row.is_editing && row[`${column.label}__error`]">
              <el-input v-model="currentEditInfo[`${column.label}`]" size="small" />
            </div>

            <div v-if="!row.is_editing || (row.is_editing && !row[`${column.label}__error`])">
              <span class="qm-import__table-text">{{ row[column.label] }}</span>
              <em class="qm-import__table-error" v-if="row[`${column.label}__error`]">数据无效</em>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100px" align="center" fixed="right">
          <template slot-scope="{ row, $index }">
            <div v-show="row.is_editing">
              <el-button type="text" size="small" :disabled="!row.is_error" @click="onSave($index)"> 保存 </el-button>
              <el-button type="text" size="small" :disabled="!row.is_error" @click="onCancel(row, $index)">
                取消
              </el-button>
            </div>
            <div v-show="!row.is_editing">
              <el-button
                type="text"
                size="small"
                :disabled="!row.is_error || !!currentEditInfo"
                @click="onEdit(row, $index)"
              >
                修改
              </el-button>
              <el-button type="text" size="small" :disabled="!row.is_error || !!currentEditInfo" @click="onDelete(row)">
                删除
              </el-button>
            </div>
          </template>
        </el-table-column>

        <template slot="empty">
          <el-image
            class="qm-import__table-empty"
            src="https://images.qmai.cn/s12416/images/2021/08/02/ba5b4194f2857a4a.png"
          />
          <p class="qm-import__table-empty-desc">暂无数据</p>
          <!--  -->
        </template>
      </el-table>

      <el-pagination
        class="qm-import-table__pagination"
        hide-on-single-page
        :current-page.sync="currentPage"
        :total="tableData.length"
        background
        layout="->, prev, pager, next"
      ></el-pagination>
    </template>

    <template v-else-if="currentStatus === 3">
      <div class="qm-import__progress">
        <el-image
          class="qm-import__progress-img"
          src="https://images.qmai.cn/resource/20210913185016/2021/10/28/1635420073276_import.png?x-oss-process=image"
          fit="cover"
          draggable="false"
        />

        <el-progress class="qm-import__progress-main" :percentage="percentage" :stroke-width="12" color="#137DED" />

        <p class="qm-import__progress-text">
          <em>正在导入</em>
          预计等待时间
          <em>{{ expectTime }}s</em>
        </p>
      </div>
    </template>

    <template v-else-if="currentStatus === 4">
      <div class="qm-import__result">
        <template v-if="$slots.result">
          <slot name="result"></slot>
        </template>
        <template v-else>
          <template v-if="importResult.failTotal > 0">
            <i class="qm-icon-warning-fill qm-import__result-icon qm-import__result-icon--warn" />
            <p class="qm-import__result-text">
              成功导入
              <em class="qm-import__result-text--success">{{ importResult.successTotal }}</em>
              条数据，失败
              <em class="qm-import__result-text--fail">{{ importResult.failTotal }}</em>
              条数据
            </p>
            <a class="qm-import__result-fail" :href="importResult.failUrl" :download="failFileName">
              下载导入失败数据
            </a>
          </template>
          <template v-else>
            <i class="qm-icon-success qm-import__result-icon qm-import__result-icon--success" />
            <p class="qm-import__result-text">
              成功导入
              <em class="qm-import__result-text--success">{{ importResult.successTotal }}</em>
              条数据
            </p>
          </template>
        </template>

        <template v-if="$slots.desc">
          <slot name="desc"></slot>
        </template>
      </div>
    </template>

    <template v-else-if="currentStatus === 5">
      <div class="qm-import__result">
        <i class="qm-icon-warning-fill qm-import__result-icon qm-import__result-icon--warn" />
        <p class="qm-import__result-text">
          <em class="qm-import__result-text--fail">{{ importResultErrorText }}</em>
        </p>
      </div>
    </template>

    <template v-if="currentStatus === 0 || currentStatus === 1 || currentStatus === 2" slot="footer">
      <el-checkbox
        v-show="currentStatus === 1 || currentStatus === 2"
        :disabled="currentStatus === 1"
        v-model="isOnlyError"
        class="qm-import__checkbox"
      >
        仅显示错误数据
      </el-checkbox>
      <div class="qm-import__footer-ple" />
      <el-button size="small" @click="onDialogCancel">取消</el-button>
      <el-button :disabled="alertInfo.successNum === 0" size="small" type="primary" @click="onImport"> 导入 </el-button>
    </template>
  </el-dialog>
</template>

<script>
import XLSX from 'xlsx'
import Schema from 'async-validator'
import { v4 as uuidv4 } from 'uuid'
import download from '@qmai/utils/lib/download'
import limitNumber from '@qmai/utils/lib/limitNumber'

export default {
  name: 'QmImport',
  props: {
    columns: {
      type: Array,
      required: true,
    },
    // 该prop已废弃
    rules: {
      type: Object,
      default: () => ({}),
    },
    templateUrl: {
      type: String,
      required: true,
    },
    templateName: {
      type: String,
      required: true,
    },
    startRow: {
      type: Number,
      default: 0, // 默认从第二行开始解析 第一行往往都是注意事项
    },
    loaded: {
      type: Number,
      required: true,
    },
    uploadDisabled: {
      type: Boolean,
    },
    visibleNotification: {
      type: Boolean,
      default: true,
    },
    beforeImport: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      stepActive: 0, // 当前进度
      currentStatus: 0, // 0-待上传 1-解析中 2-待导入 3-导入中 4-导入成功 5-导入失败

      fileName: '', // 上传文件 fileName
      fileType: '', // 上传文件的 fileType
      sheetName: '', // 上传xls的 sheetName
      isOnlyError: true, // 是否只显示错误信息
      originTableData: [], // 源数据
      currentPage: 1, // 当前页
      currentEditInfo: null, // 当前编辑信息
      validate: null, // 验证方法

      percentage: 0, // 上传进度 百分比
      fileSize: 0, // 生成的file文件 size记录
      expectTime: 0, // 预计时间

      // 最后结果
      importResult: {
        successTotal: 0,
        failTotal: 0,
        failUrl: '',
      },
      importResultErrorText: '', // 导入后台解析错误 返回的错误信息
      failFileName: '', // 导入失败下载时的名称
    }
  },
  computed: {
    $uploadDisabled() {
      return this.uploadDisabled
    },
    // 弹窗的宽
    dialogWidth() {
      const allColumnsWidth = this.columns.reduce((p, c) => p + (c.minWidth || 100), 20 * 2)
      return this.currentStatus === 1 || this.currentStatus === 2
        ? limitNumber(allColumnsWidth, 760, 1200) + 'px'
        : '580px'
    },
    tableData() {
      return this.isOnlyError ? this.originTableData.filter(item => item.is_error) : this.originTableData
    },
    paginationData() {
      return this.tableData.slice((this.currentPage - 1) * 10, this.currentPage * 10)
    },
    // 错误信息
    alertInfo() {
      const allNum = this.originTableData.length
      const errNum = this.originTableData.filter(item => item.is_error).length
      const successNum = allNum - errNum
      return {
        allNum,
        errNum,
        successNum,
      }
    },
  },
  watch: {
    loaded(current, prev) {
      if (current === 0) return

      // 换算成当前进度
      this.percentage = Math.min(Math.ceil((current / this.fileSize) * 100), 100)
    },
    columns: {
      immediate: true,
      handler(current, prev) {
        // 获取规则
        const columnRules = this.columns.reduce((prev, current) => {
          prev[current.label] = current.rules
          return prev
        }, {})

        const rules = { ...columnRules, ...this.rules }

        // 生成验证对象
        const validator = new Schema(rules)

        this.validate = source =>
          new window.Promise((resolve, reject) => {
            validator.validate(source, { suppressWarning: false, first: false, firstFields: false }, errors =>
              errors ? reject(errors) : resolve()
            )
          })
      },
    },
  },
  methods: {
    // 以二进制读取内容
    _getFileContent(file) {
      return new window.Promise((resolve, reject) => {
        try {
          const reader = new FileReader()
          reader.onload = ({ target }) => resolve(target)
          reader.readAsBinaryString(file)
        } catch (error) {
          reject(error)
        }
      })
    },
    // 删除行
    _deleteRow(ws, index) {
      function _encodeCell(r, c) {
        return XLSX.utils.encode_cell({ r, c })
      }

      const range = XLSX.utils.decode_range(ws['!ref'])

      for (let row = index; row < range.e.r; row++) {
        for (let col = range.s.c; col <= range.e.c; col++) {
          ws[_encodeCell(row, col)] = ws[_encodeCell(row + 1, col)]
        }
      }

      range.e.r--

      ws['!ref'] = XLSX.utils.encode_range(range.s, range.e)
    },
    // 二进制转sheet
    _binaryToSheet(binaryString) {
      const { SheetNames, Sheets } = XLSX.read(binaryString, {
        type: 'binary',
        codepage: 936,
      })
      const sheetName = SheetNames[0]
      const workSheet = Sheets[sheetName]

      return {
        sheetName,
        workSheet,
      }
    },
    // sheet转Blob
    _sheetToBlob(sheet, sheetName) {
      // 字符串转ArrayBuffer
      function s2ab(s) {
        const buf = new window.ArrayBuffer(s.length)
        const view = new window.Uint8Array(buf)
        for (let i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xff
        return buf
      }

      sheetName = sheetName || 'sheet1'
      const workbook = {
        SheetNames: [sheetName],
        Sheets: {},
      }
      workbook.Sheets[sheetName] = sheet
      // 生成excel的配置项
      const wopts = {
        bookType: 'xlsx', // 要生成的文件类型
        bookSST: false, // 是否生成Shared String Table，官方解释是，如果开启生成速度会下降，但在低版本IOS设备上有更好的兼容性
        type: 'binary',
      }
      const wbout = XLSX.write(workbook, wopts)
      const blob = new Blob([s2ab(wbout)], {
        type: 'application/octet-stream',
      })

      return blob
    },
    // 文件上传
    onUploadChange({ raw }) {
      // 状态更改为解析中
      this.currentStatus = 1
      // 记录filename 和 fileType
      this.fileName = raw.name
      this.fileType = raw.type

      // 为了动画 因为css有 0.25s的过渡动画 推到宏任务队列
      setTimeout(() => {
        this._getFileContent(raw).then(({ result: binaryString }) => {
          // 获取到 sheetname 和 workSheet
          const { sheetName, workSheet } = this._binaryToSheet(binaryString)
          // 根据开始行 去除
          for (let index = 0; index < this.startRow; index++) {
            this._deleteRow(workSheet, index)
          }
          // 转化为json
          const data = XLSX.utils.sheet_to_json(workSheet, { defval: '' })
          // fix: 在未知情况下 会解析出空数据会解析 __EMPTY 已知是上一行的defval的问题 但是不好去除 涉及到编辑时候的显示问题
          data.forEach(item => {
            for (const key in item) {
              if (Object.hasOwnProperty.call(item, key) && key.indexOf('__EMPTY') === 0) {
                delete item[key]
              }
            }
          })
          // 存储sheetName
          this.sheetName = sheetName
          // 数据清洗
          const result = []

          for (let item of data) {
            this.validate(item)
              .then()
              .catch(errors => {
                errors.forEach(error => (item[`${error.field}__error`] = true))
                item.is_error = true
              })
              .finally(() => {
                item.is_editing = false
                item.is_qmId = uuidv4()
                if (!item.is_error) item = Object.freeze(item)
                result.push(item)
              })
          }

          setTimeout(() => {
            // 错误排序在前
            result.sort((a, b) => {
              if (a.is_error && !b.is_error) {
                return -1
              } else if ((a.is_error && b.is_error) || (!a.is_error && !b.is_error)) {
                return 0
              } else {
                return 1
              }
            })

            // 赋予值
            this.originTableData.push(...result)

            // 查询是否有错误条目
            const errorInfo = result.find(item => item.is_error)

            if (errorInfo) {
              // 状态更改为待导入
              this.currentStatus = 2
            } else {
              // 没有错误项 直接拉起导入
              this.onImport()
            }
          }, 0)
        })
      }, 300)
    },
    onEdit(row) {
      this.$set(this, 'currentEditInfo', { ...row })
      row.is_editing = true
    },
    onCancel(row) {
      row.is_editing = false
      this.$set(this, 'currentEditInfo', null)
    },
    onSave() {
      const currentEditInfo = { ...this.currentEditInfo }

      // 还原成一个验证前的对象
      currentEditInfo.is_editing = false
      delete currentEditInfo.is_error
      for (const key in currentEditInfo) {
        if (key.endsWith('__error')) {
          delete currentEditInfo[key]
        }
      }

      // 再次验证
      this.validate(currentEditInfo)
        .then()
        .catch(errors => {
          errors.forEach(error => (currentEditInfo[`${error.field}__error`] = true))
          currentEditInfo.is_error = true
        })
        .finally(() => {
          const currentIndex = this.originTableData.findIndex(item => item.is_qmId === currentEditInfo.is_qmId)

          if (currentIndex >= 0) {
            this.originTableData.splice(currentIndex, 1, currentEditInfo)
          }

          this.$set(this, 'currentEditInfo', null)
        })
    },
    onDelete(row) {
      const currentIndex = this.originTableData.findIndex(item => item.is_qmId === row.is_qmId)

      if (currentIndex >= 0) {
        this.originTableData.splice(currentIndex, 1)
      }
    },
    onDialogCancel() {
      this.$emit('update:visible', false)
    },
    onClose() {
      this.stepActive = 0
      this.currentStatus = 0
      this.fileName = ''
      this.fileType = ''
      this.sheetName = ''
      this.isOnlyError = true
      this.$set(this, 'originTableData', [])
      this.$set(this, 'currentEditInfo', null)

      this.percentage = 0
      this.fileSize = 0
      this.expectTime = 0
      this.$emit('update:loaded', 0)

      this.$set(this, 'importResult', {
        successTotal: 0,
        failTotal: 0,
        failUrl: '',
      })
    },

    // 分析导入时间
    analysisTime(allTotal) {
      const rnd = (n, m) => Math.floor(Math.random() * (m - n + 1) + n)

      if (allTotal <= 100) {
        return rnd(1, 3)
      } else if (allTotal > 100 && allTotal <= 500) {
        return rnd(5, 7.5)
      } else if (allTotal > 500 && allTotal <= 1000) {
        return rnd(10, 20)
      } else if (allTotal > 1000 && allTotal <= 2000) {
        return rnd(30, 45)
      } else if (allTotal > 1000 && allTotal <= 4000) {
        return rnd(60, 90)
      } else {
        return rnd(120, 180)
      }
    },

    onImport() {
      // 更改状态为 导入中
      this.currentStatus = 3

      // 获取正确的数据 并进行清洗
      const tableInfo = this.originTableData
        .filter(item => !item.is_error)
        .map(item => {
          const newItem = { ...item } // 切断get set
          delete newItem.is_editing
          delete newItem.is_qmId
          return newItem
        })

      let newFile = null // 新文件对象

      if (this.fileType === 'text/csv') {
        // json转sheet
        const workSheet = XLSX.utils.json_to_sheet(tableInfo)
        // sheet转csv
        const csvStr = XLSX.utils.sheet_to_csv(workSheet)
        // 转化为blob
        const sheetBlob = new Blob([csvStr])
        // blob转file对象
        newFile = new File([sheetBlob], this.fileName, { type: this.fileType })
      } else {
        const workSheet = XLSX.utils.json_to_sheet(tableInfo)
        // 转化为blob
        const sheetBlob = this._sheetToBlob(workSheet, this.sheetName)
        // blob转file对象
        newFile = new File([sheetBlob], this.fileName, { type: this.fileType })
      }

      // 记录文件大小
      this.fileSize = newFile.size

      // 当前进度为 数据导入中
      this.stepActive = 1

      this.expectTime = this.analysisTime(this.alertInfo.successNum)

      this.beforeImport(newFile)
        .then(result => {
          this.$set(this, 'importResult', result)

          if (this.$attrs.visible) {
            // 导入成功状态
            this.currentStatus = 4

            // 当前进度为 完成导入
            this.stepActive = 2
          } else if (this.visibleNotification) {
            const resultDefaultText = `成功导入<span style="color: #1CC700;">${result.successTotal}</span>条数据`
            const errorText = `，失败<span style="color: #FF1313;">${result.failTotal}</span>条数据
                <p class="qm-import-result-notify__download">
                  <a class="qm-import-result-notify__download-link" href="${result.failUrl}"  download="${this.failFileName}" >下载导入失败数据</a>
                </p>`
            this.$notify({
              customClass: `qm-import-result-notify ${result.failTotal === 0 && 'is-only-success'}`,
              title: '导入任务完成',
              message: resultDefaultText + (result.failTotal > 0 ? errorText : ''),
              type: 'success',
              duration: 5000,
              dangerouslyUseHTMLString: true,
            })
          }
        })
        .catch(error => {
          if (this.$attrs.visible) {
            // 记录错误信息
            this.importResultErrorText = error
            // 导入失败状态
            this.currentStatus = 5
            // 当前进度为 完成导入
            this.stepActive = 2
          } else {
            this.$notify({
              title: '导入任务失败',
              message: error,
              type: 'error',
            })
          }
        })
    },

    // 下载模版
    onDownloadTemplate() {
      download(this.templateUrl, this.templateName)
    },
  },
  created() {
    // 错误数据名称
    const errNameArr = this.templateName.split('.')
    errNameArr[errNameArr.length - 2] += '失败数据'
    this.failFileName = errNameArr.splice(errNameArr.length - 1, 0, '失败数据').join('.')
  },
}
</script>
